import { Drawer, Toolbar, Box, useMediaQuery, Typography, Divider, Chip, CircularProgress } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { TABS } from './AdminTabsList';
import { DrawerList } from '../components';
import { useTranslation } from 'react-i18next';
import { getWaitingCount } from "src/_store/entities/admins";
import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useGetWithdrawalsTransactionsCountQuery } from 'src/_api/mapitShipInvoice';
import { useGetSatchelsRequestsCountQuery } from 'src/_api/satchelApi';
import { useAdminCountApprovelandWaitingQuery } from 'src/_api/adminApi';
import useAiEligibility from 'src/_modules/AiSupportChat/hooks/useAiEligibility';

const drawerWidth = 280;

/**
 * Drops the tabs marked `requiresAiDeveloper`, at any depth. Builds new arrays
 * and copies only the tabs whose children change: `TABS` is shared.
 */
const withoutAiDeveloperTabs = (tabs) => {
    let changed = false;
    const kept = [];
    tabs.forEach(tab => {
        if (tab.requiresAiDeveloper) {
            changed = true;
            return;
        }
        const children = tab.children && withoutAiDeveloperTabs(tab.children);
        if (children !== tab.children) {
            changed = true;
            kept.push({ ...tab, children });
        } else {
            kept.push(tab);
        }
    });
    return changed ? kept : tabs;
};

export default function AdminDrawer({ openDrawer, toggleDrawer }) {
    const { pathname } = useLocation();
    const { t } = useTranslation()
    const downLg = useMediaQuery(theme => theme.breakpoints.down('lg'));
    const dispatch = useDispatch()
    const { waitingCount, waitingCountPending } = useSelector(state => state.entities.admins)

    const { data: withdrawalsData } = useGetWithdrawalsTransactionsCountQuery()
    const { data: satchelsData } = useGetSatchelsRequestsCountQuery()
    // console.log(satchelsData);
    
    const {data: approvedData} = useAdminCountApprovelandWaitingQuery()
     console.log(approvedData?.body?.commercialDataReviewCount);
            
    useEffect(() => {
        dispatch(getWaitingCount())
    }, [])

    useEffect(() => {
        if (downLg && openDrawer) {
            toggleDrawer();
        }
    }, [pathname, downLg]);


    const tabsList = useMemo(() => {
        const withdrawalCodTransactionCount = withdrawalsData || 0;
        const waitingSatchelsCount = satchelsData || 0;
        const waitingApprovedCount = approvedData?.body?.commercialDataReviewCount || 0;
        
        if (withdrawalCodTransactionCount > 0 || waitingSatchelsCount > 0  || waitingApprovedCount > 0) {
            return TABS.map(tab => {
                if (tab.title === "Users") {
                    tab.secondaryAction = {
                        content:waitingApprovedCount ,
                        color: "warning"
                    }
                    return {
                        ...tab,
                        children: tab.children.map(child => {
                            if (child.href === "/admin/approvel") {
                                return {
                                    ...child,
                                    secondaryAction: {
                                        content: waitingApprovedCount,
                                        color: "warning"
                                    }
                                };
                            }
                            return child;
                        })
                    };
                }
                if (tab.title === "Orders") {
                    tab.secondaryAction = {
                        content: waitingSatchelsCount,
                        color: "warning"
                    }
                    return {
                        ...tab,
                        children: tab.children.map(child => {
                            if (child.href === "/admin/aramex-satchels?status=ORDER_ASSIGNED_TO_SHIPPING_COMPANY") {
                                return {
                                    ...child,
                                    secondaryAction: {
                                        content: waitingSatchelsCount,
                                        color: "warning"
                                    }
                                };
                            }
                            return child;
                        })
                    };
                }

                if (tab.title === "Mapit Ship (COD)") {
                    tab.secondaryAction = {
                        content: withdrawalCodTransactionCount,
                        color: "warning"
                    }
                    return {
                        ...tab,
                        children: tab.children.map(child => {
                            if (child.href === "/admin/mapit-ship-cod/withdrawal-requests") {
                                return {
                                    ...child,
                                    secondaryAction: {
                                        content: withdrawalCodTransactionCount,
                                        color: "warning"
                                    }
                                };
                            }
                            return child;
                        })
                    };
                }
                return tab;
            });
        }
        return TABS;
    }, [withdrawalsData, satchelsData , approvedData?.body?.commercialDataReviewCount])

    // Same cached eligibility entry as the chat launcher; the backend enforces access anyway
    const { isAiDeveloper } = useAiEligibility()
    const visibleTabs = useMemo(
        () => (isAiDeveloper ? tabsList : withoutAiDeveloperTabs(tabsList)),
        [tabsList, isAiDeveloper]
    )


    return (
        <Drawer
            open={openDrawer}
            onClose={toggleDrawer}
            variant={downLg ? "temporary" : "permanent"}
            sx={{
                width: drawerWidth,
                flexShrink: 0,
                [`& .MuiDrawer-paper`]: { width: drawerWidth, boxSizing: 'border-box' },
            }}
        >
            <Toolbar />
            <Box sx={{ overflowY: 'auto', overflowX: 'hidden' }}>
                <Box
                    sx={{
                        alignItems: "center",
                        display: "flex",
                        flexDirection: "column",
                        py: 2,
                    }}
                    bgcolor="main.dark"
                >
                    <Typography color="text.white" variant="h5">
                        {t("Welcome")}
                    </Typography>
                    <Box display="flex" width="100%" my={1} sx={{ borderTop: "1px solid white" }} />
                    <Typography color="text.white" variant="subtitle1" sx={{ textAlign: 'center' }}>
                        {t("Waiting for review")}
                    </Typography>
                    <Typography color="text.white" variant="h6" sx={{ textAlign: 'center' }}>
                        {waitingCountPending ? <CircularProgress size={20} sx={{ color: "text.white" }} />
                            : <Chip sx={{ color: "text.white" }} variant="outlined" label={approvedData?.body?.waitingCount}></Chip>}
                    </Typography>
                </Box>
                <Divider />
                <DrawerList tabs={visibleTabs} />
            </Box>
        </Drawer>
    )
}
